import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useModal } from 'react-modal-hook';
import { useParams } from 'react-router';
import {
	ProductsLayoutTypesBold,
	ProductsLayoutTypesClassic,
	ProductsLayoutTypesFun,
	TemplateClassNames,
} from '../../../../types/template-layouts';
import { ModuleMap, PageModule, PageModuleType, Product } from '../../../../types/working-model';
import { Button } from '../../../general-ui/button/button';
import { EIcon } from '../../../general-ui/icon/icon';
import SelectNative from '../../../general-ui/select/select-native';
import { ProductDetailsModal } from '../../containers/product-details-modal/product-details.modal';
import { ParamsProps } from '../../live-event';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { getStylingOverrides } from '../../utils';
import ProductItem from './product-item';
import './products.scss';
import { getTextValue } from '../../../../utils/utils';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

interface IAttribute {
	type: string;
	value: string;
}

interface ProductsProps {
	module: PageModule;
	template: TemplateClassNames;
}

const Products = React.forwardRef<HTMLDivElement, ProductsProps>(({ module, template }, ref) => {
	const [product, setProduct] = useState<Product>();
	const [selectedCategory, setSelectedCategory] = useState('');
	const [selectedAttribute, setSelectedAttribute] = useState('');

	const params: ParamsProps = useParams();
	const language = params.language || 'en';
	const { content, styling_overrides, modules } = module;
	const layout_type = content?.layout_type || ProductsLayoutTypesClassic.standard;
	//init as one variable so it can be passed to the card
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		backgroundColor,
		backgroundImg,
		customHeadingOverrides,
		titleOverrides,
		descriptionOverrides,
		dropdownOverrides,
		secondaryButtonOverrides,
	} = stylingOverrides;
	const [showMore, setShowMore] = useState(false);
	const { t } = useTranslation("homepage");
	const [showProductDetailsModal, hideProductDetailsModal] = useModal(
		() => (
			<ProductDetailsModal
				template={template}
				layout_type={layout_type}
				onClose={hideProductDetailsModal}
				product={product as Product}
				stylingOverrides={stylingOverrides}
				language={language}
			/>
		),
		[product, template, layout_type, language]
	);

	const maxProducts = useMemo(() => {
		switch (layout_type) {
			case ProductsLayoutTypesClassic.standard:
			case ProductsLayoutTypesBold.standard:
			case ProductsLayoutTypesFun.standard:
				return 3;

			case ProductsLayoutTypesClassic.grid:
			case ProductsLayoutTypesBold.grid:
			case ProductsLayoutTypesFun.grid:
				return 4;

			case ProductsLayoutTypesClassic.vertical_list:
			case ProductsLayoutTypesBold.vertical_list:
			case ProductsLayoutTypesFun.vertical_list:
				return 4;

			default:
				return 2;
		}
	}, [layout_type]);

	const productsCount = modules?.length ?? maxProducts;

	const limitProductsIndex = useCallback(() => {
		if (showMore) return productsCount;
		else return maxProducts;
	}, [showMore, productsCount, maxProducts]);

	function handleShowMore() {
		setShowMore((prev) => !prev);
	}

	const { custom_heading, description, title } = getTextValue(content, language);

	const productCategories = useMemo(() => {
		if (!modules?.length) return [];
		const allCategories: string[] = [];
		modules?.forEach((module: Product) => {
			allCategories.push(...module.categories);
		});
		// convert to set and then back to array to ensure all strings are unique
		return Array.from(new Set(allCategories)).map((category: string) => ({
			label: category,
			value: category,
		}));
	}, [modules]);

	// because product attributes are key/value pairs and the filter list is a single list,
	// we need to filter both the key and value to validate them together.
	const productAttributes = useMemo(() => {
		if (!modules?.length) return [];
		const allAttributes: { label: string, value: string }[] = [];
		const existsMap: { [key: string]: any } = {};
		modules?.forEach((module: Product) => {
			if (module?.attributes?.length) {
				module.attributes.forEach((attribute: IAttribute) => {
					const key = attribute.type?.trim()?.toLowerCase();
					const value = attribute.value?.trim()?.toLowerCase();
					if (key && value) {
						if (!existsMap?.[key]) {
							existsMap[key] = {};
						}
						if (!existsMap[key]?.[value]) {
							existsMap[key][value] = value;
							allAttributes.push({
								label: `${key} / ${value}`,
								value: `${key} / ${value}`,
							});
						}
					}
				});
			}
		});
		return allAttributes;
	}, [modules]);

	const handleAttributeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedAttribute(e.target.value);
	};

	const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedCategory(e.target.value);
	};

	const itemsToRender = useCallback(() => {
		if (!modules?.length) return [];
		let itemsToRender = [...modules];
		if (selectedCategory || selectedAttribute) {
			itemsToRender = itemsToRender.filter((module: Product) => {

				// if there's a selected category, then find it, otherwise set it to true
				const matchedCategory = selectedCategory ? module.categories.find(category => {
					return category?.toLowerCase().includes(selectedCategory.toLowerCase());
				}) : true;

				// if there's a selected attribute, then find it, otherwise set it to true
				const matchedAttribute = selectedAttribute ? module.attributes.find((attribute: IAttribute) => {
					const key = attribute.type?.trim()?.toLowerCase();
					const value = attribute.value?.trim()?.toLowerCase();
					if (`${key} / ${value}` === selectedAttribute) return true;
					return false;
				}) : true;

				// if both both category and attribute are true, then return the product
				return matchedCategory && matchedAttribute;
			});
		}
		return itemsToRender;
	}, [modules, selectedCategory, selectedAttribute]);

	return module.is_on && content && (
		<section
			id={ModuleMap[PageModuleType.products]}
			className={classNames(
				'products',
				`products-${layout_type}`,
				template,
				backgroundColor
			)}
			ref={ref}
		>
			<div className="module-container" style={backgroundImg}>
				<div className="products-text-wrapper">
					<TypographyItem className={classNames('evt-caption-1 products-custom-heading', customHeadingOverrides)} tagName="p">
						{custom_heading}
					</TypographyItem>
					<div className="products-top">
						<div className="text-container">
							<TypographyItem className={classNames('evt-heading-2 products-title', titleOverrides)} tagName="h2">
								{title}
							</TypographyItem>
							<TypographyItem className={classNames('evt-body-text-1 products-description')}>
								<ReactMarkdown className={descriptionOverrides} source={description} />
							</TypographyItem>
						</div>
						<div className="dropdown-container">
							<SelectNative
								className={classNames('products-dropdown', dropdownOverrides)}
								options={[{ label: t('All attributes'), value: '' }, ...productAttributes]}
								onChange={handleAttributeChange}
							/>
							<SelectNative
								className={classNames('products-dropdown', dropdownOverrides)}
								options={[{ label: t('All categories'), value: '' }, ...productCategories]}
								onChange={handleCategoryChange}
							/>
						</div>
					</div>
				</div>
				<ul className="product-list">
					{itemsToRender()?.slice(0, limitProductsIndex()).map((module: Product) => (
						<ProductItem
							key={module.product}
							module={module}
							stylingOverrides={stylingOverrides}
							template={template}
							onClick={(product) => {
								setProduct(product);
								showProductDetailsModal();
							}}
						/>
					))}
				</ul>
				{itemsToRender()?.length > maxProducts ? (
					<Button
						classButton={classNames(
							'secondary',
							secondaryButtonOverrides
						)}
						template={template}
						onClick={handleShowMore}
					>
						{template == TemplateClassNames.Apollo && (
							<EIcon
								name="refresh-transparent"
								position="order-left"
								size="medium"
							/>
						)}
						{t("Show")} {showMore ? t('Less') : t('More')}
					</Button>
				) : null}
			</div>
		</section>
	);
});

Products.displayName = 'Products';

export default Products;
